import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  List, 
  ListItem, 
  ListItemText, 
  Modal, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  IconButton, 
  Typography, 
  Chip, 
  Pagination, 
  Alert, 
  Snackbar, 
  CircularProgress,
  Divider,
  Paper
} from '@mui/material';
import { 
  Edit as EditOutlinedIcon, 
  Delete as DeleteOutlinedIcon, 
  Add as AddIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import MedicoService from '../../service/medicoService';
import './serviciosMedico.css';

const PAGE_SIZE = 3;
const ESPECIALIDADES = [
  'CARDIOLOGIA',
  'TRAUMATOLOGIA',
  'PEDIATRIA',
  'DERMATOLOGIA',
  'GINECOLOGIA',
  'OFTALMOLOGIA',
  'NEUROLOGIA',
  'PSIQUIATRIA',
  'ONCOLOGIA',
  'ENDOCRINOLOGIA',
  'CLINICA MEDICA',
  'CIRUGIA GENERAL',
  'UROLOGIA',
  'GASTROENTEROLOGIA',
  'NEUMONOLOGIA',
  'REUMATOLOGIA',
  'HEMATOLOGIA',
  'INFECTOLOGIA',
  'NEFROLOGIA',
  'ALERGIA'
];

const PRACTICAS = [
  'RADIOLOGIA',
  'ECOGRAFIA',
  'TOMOGRAFIA',
  'RESONANCIA',
  'LABORATORIO',
  'ELECTROCARDIOGRAMA',
  'ESPIRIOMETRIA',
  'MAMOGRAFIA',
  'PAPANICOLAOU',
  'COLONOSCOPIA',
  'ENDOSCOPIA',
  'ERGOMETRIA',
  'ELECTROENCEFALOGRAMA',
  'DOPPLER',
  'DENSITOMETRIA'
];

const mapearServiciosDelMedico = (medico) => {
  if (!medico) return [];
  const especialidades = (medico.especialidades || []).map(e => ({
    ...e,
    tipo: 'Especialidad',
    id: `esp-${e.nombre}`
  }));
  const practicas = (medico.practicas || []).map(p => ({
    ...p,
    tipo: 'Práctica',
    id: `prac-${p.nombre}`
  }));
  return [...especialidades, ...practicas];
};

const ListaServicios = ({ medico, onUpdateMedico }) => {
  const [todosMisServicios, setTodosMisServicios] = useState([]);
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ tipo: '', nombre: '', duracionEnMinutos: 30, costo: 0 });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);

  const [isSedeModalOpen, setIsSedeModalOpen] = useState(false);
  const [sedeFormData, setSedeFormData] = useState({ nombre: '', direccion: '' });
  const [sedeToDelete, setSedeToDelete] = useState(null);
  const [deleteSedeConfirmOpen, setDeleteSedeConfirmOpen] = useState(false);

  useEffect(() => {
    if (medico) {
      const serviciosUnificados = mapearServiciosDelMedico(medico);
      setTodosMisServicios(serviciosUnificados);
      setPage(1);
    }
  }, [medico]);

  const getPaginatedData = () => {
    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return todosMisServicios.slice(start, end);
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({ tipo: 'Especialidad', nombre: ESPECIALIDADES[0], duracionEnMinutos: 30, costo: 0 });
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      tipo: item.tipo,
      nombre: item.nombre,
      duracionEnMinutos: item.duracionEnMinutos || 30,
      costo: item.costo || 0
    });
    setIsModalOpen(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    const idMedico = medico?.id || medico?._id;
    if (!idMedico) {
      setSnackbar({ open: true, message: 'No se pudo identificar el ID del médico', severity: 'error' });
      return;
    }
    const tipoServicio = itemToDelete.tipo === 'Especialidad' ? 'ESPECIALIDAD' : 'PRACTICA';
    try {
      setLoading(true);
      const medicoActualizado = await MedicoService.eliminarServicio(idMedico, tipoServicio, itemToDelete.nombre);
      if (onUpdateMedico) onUpdateMedico(medicoActualizado);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      setSnackbar({ open: true, message: 'Servicio eliminado correctamente', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Error al eliminar el servicio', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

 const handleSave = async () => {
    if (!formData.nombre || !formData.duracionEnMinutos) {
        setSnackbar({ open: true, message: 'Completa todos los campos', severity: 'error' });
        return;
    }

    const idMedico = medico?.id || medico?._id;
    if (!idMedico) {
        setSnackbar({ open: true, message: 'No se pudo identificar el ID del médico', severity: 'error' });
        return;
    }

    const tipoServicio = formData.tipo === 'Especialidad' ? 'ESPECIALIDAD' : 'PRACTICA';
    try {
        setLoading(true);
        let medicoActualizado;
        if (editingItem) {
            medicoActualizado = await MedicoService.modificarServicio(
                idMedico,
                tipoServicio,
                editingItem.nombre,
                formData.nombre,
                formData.duracionEnMinutos,
                formData.costo
            );
            setSnackbar({ open: true, message: 'Servicio actualizado correctamente', severity: 'success' });
        } else {
            medicoActualizado = await MedicoService.agregarServicio(
                idMedico,
                tipoServicio,
                formData.nombre,
                formData.duracionEnMinutos,
                formData.costo
            );
            setSnackbar({ open: true, message: 'Servicio agregado correctamente', severity: 'success' });
        }
        if (onUpdateMedico) onUpdateMedico(medicoActualizado);
        setIsModalOpen(false);
    } catch (error) {
        setSnackbar({ open: true, message: error.message || 'Error al guardar el servicio', severity: 'error' });
    } finally {
        setLoading(false);
    }
};

  const openSedeModal = () => {
    setSedeFormData({ nombre: '', direccion: '' });
    setIsSedeModalOpen(true);
  };

  const handleAddSede = async () => {
    if (!sedeFormData.nombre.trim() || !sedeFormData.direccion.trim()) {
        setSnackbar({ open: true, message: 'Completa todos los campos de la sede', severity: 'error' });
        return;
    }

    const idMedico = medico?.id || medico?._id;
    if (!idMedico) {
        setSnackbar({ open: true, message: 'No se pudo identificar el ID del médico', severity: 'error' });
        return;
    }

    try {
        setLoading(true);
        const medicoActualizado = await MedicoService.agregarSede(idMedico, {
            nombre: sedeFormData.nombre.trim(),
            direccion: sedeFormData.direccion.trim()
        });
        
        if (onUpdateMedico) onUpdateMedico(medicoActualizado);
        setIsSedeModalOpen(false);
        setSnackbar({ open: true, message: 'Sede agregada correctamente', severity: 'success' });
    } catch (error) {
        setSnackbar({ open: true, message: error.message || 'Error al agregar la sede', severity: 'error' });
    } finally {
        setLoading(false);
    }
};


  const handleDeleteSedeClick = (sede) => {
    setSedeToDelete(sede);
    setDeleteSedeConfirmOpen(true);
  };

  const handleDeleteSede = async () => {
    const idMedico = medico?.id || medico?._id;
    if (!idMedico) {
      setSnackbar({ open: true, message: 'No se pudo identificar el ID del médico', severity: 'error' });
      return;
    }

    try {
      setLoading(true);
      const medicoActualizado = await MedicoService.eliminarSede(idMedico, sedeToDelete.nombre);
      
      if (onUpdateMedico) onUpdateMedico(medicoActualizado);
      setDeleteSedeConfirmOpen(false);
      setSedeToDelete(null);
      setSnackbar({ open: true, message: 'Sede eliminada correctamente', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Error al eliminar la sede', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const currentPageData = getPaginatedData();

  return (
    <div className="medico-config-containers">
      <div className="medico-config-headers">
        <div>
          <Typography variant="h5">Mis Servicios Médicos</Typography>
          <Typography variant="body2" className="medico-nombre">
            {medico?.nombre}
          </Typography>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
            onClick={openCreateModal}
            disabled={loading}
            className="custom-button"
          >
            Agregar Servicio
          </Button>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LocationIcon />}
            onClick={openSedeModal}
            disabled={loading}
          >
            Agregar Sede
          </Button>
        </div>
      </div>

      <Paper elevation={1} className="sedes-section" style={{ marginBottom: '24px', padding: '20px', backgroundColor: '#ffffff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Typography variant="subtitle1" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.1rem', fontWeight: 500 }}>
            <HomeIcon color="primary" />
            Mis Sedes
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.95rem' }}>
            {medico?.sedes?.length || 0} sedes configuradas
          </Typography>
        </div>
        {(!medico?.sedes || medico.sedes.length === 0) ? (
          <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: '12px', fontSize: '0.95rem' }}>
            No tienes sedes configuradas. Agrega una para poder asignar disponibilidades.
          </Typography>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {medico.sedes.map((sede, index) => (
              <Chip
                key={index}
                label={`${sede.nombre} - ${sede.direccion}`}
                onDelete={() => handleDeleteSedeClick(sede)}
                deleteIcon={<DeleteOutlinedIcon />}
                variant="outlined"
                style={{ 
                  maxWidth: '100%', 
                  fontSize: '1rem', 
                  padding: '4px 8px',
                  height: '40px'
                }}
                sx={{
                  '& .MuiChip-label': {
                    fontSize: '1rem',
                    padding: '0 12px'
                  },
                  '& .MuiChip-deleteIcon': {
                    fontSize: '1.2rem'
                  }
                }}
              />
            ))}
          </div>
        )}
      </Paper>

      <List className="servicios-list">
        {currentPageData.length === 0 ? (
          <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
            No posees servicios médicos registrados aún.
          </Typography>
        ) : (
          currentPageData.map((item) => (
            <ListItem
              key={item.id}
              className="servicio-item"
              secondaryAction={
                <div className="item-actions">
                  <IconButton
                    onClick={() => openEditModal(item)}
                    className="edit-button"
                    disabled={loading}
                  >
                    <EditOutlinedIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteClick(item)}
                    className="delete-button"
                    disabled={loading}
                  >
                    <DeleteOutlinedIcon />
                  </IconButton>
                </div>
              }
            >
              <ListItemText
                primary={
                  <div className="servicio-title">
                    <Typography className="servicio-nombre">
                      {item.nombre}
                    </Typography>
                    <Chip
                      label={item.tipo}
                      className={item.tipo === 'Especialidad' ? 'tag-especialidad' : 'tag-practica'}
                      variant="outlined"
                    />
                  </div>
                }
                secondary={
                  <div>
                    <Typography className="servicio-duracion">
                      Duración asignada para turnos: {item.duracionEnMinutos} minutos
                    </Typography>
                    <Typography className="servicio-duracion">
                      Costo: ${item.costo || 0}
                    </Typography>
                  </div>
                }
              />
            </ListItem>
          ))
        )}
      </List>

      {todosMisServicios.length > PAGE_SIZE && (
        <div className="pagination-container">
          <Pagination
            count={Math.ceil(todosMisServicios.length / PAGE_SIZE)}
            page={page}
            onChange={(event, value) => setPage(value)}
            className="custom-pagination"
            disabled={loading}
          />
        </div>
      )}

      <Modal open={isModalOpen} onClose={() => !loading && setIsModalOpen(false)}>
        <div className="modal-container">
          <Typography variant="h6" className="modal-title">
            {editingItem ? "Modificar Servicio" : "Agregar Nuevo Servicio"}
          </Typography>

          <FormControl fullWidth className="form-field" disabled={loading}>
            <InputLabel>Tipo de Categoría</InputLabel>
              <Select
                value={formData.tipo}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tipo: e.target.value,
                    nombre:
                      e.target.value === 'Especialidad'
                        ? ESPECIALIDADES[0]
                        : PRACTICAS[0]
                  })
                }
                label="Tipo de Categoría"
              >
                <MenuItem value="Especialidad">Especialidad</MenuItem>
                <MenuItem value="Práctica">Práctica</MenuItem>
              </Select>
          </FormControl>

                <FormControl fullWidth className="form-field" disabled={loading}>
                  <InputLabel>Servicio</InputLabel>
                  <Select
                    value={formData.nombre}
                    label="Servicio"
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        nombre: e.target.value
                      })
                    }
                  >
                    {(formData.tipo === 'Especialidad'
                      ? ESPECIALIDADES
                      : PRACTICAS
                    ).map((opcion) => (
                      <MenuItem
                        key={opcion}
                        value={opcion}
                      >
                        {opcion}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

          <TextField
            fullWidth
            label="Duración del Turno (en minutos)"
            type="number"
            value={formData.duracionEnMinutos}
            onChange={(e) => setFormData({ ...formData, duracionEnMinutos: parseInt(e.target.value) || 5 })}
            slotProps={{ input: { min: 5, step: 5 } }}
            className="form-field"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Costo ($) - Opcional"
            type="number"
            value={formData.costo}
            onChange={(e) => setFormData({ ...formData, costo: parseFloat(e.target.value) || 0 })}
            slotProps={{ input: { min: 0, step: 100 } }}
            className="form-field"
            disabled={loading}
          />

          <div className="modal-actions">
            <Button onClick={() => setIsModalOpen(false)} className="cancel-button" disabled={loading}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSave} className="custom-button" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Guardar"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={isSedeModalOpen} onClose={() => !loading && setIsSedeModalOpen(false)}>
        <div className="modal-container" style={{ maxWidth: '500px' }}>
          <Typography variant="h6" className="modal-title">
            Agregar Nueva Sede
          </Typography>

          <TextField
            fullWidth
            label="Nombre de la Sede"
            value={sedeFormData.nombre}
            onChange={(e) => setSedeFormData({ ...sedeFormData, nombre: e.target.value })}
            placeholder="Ej: Sede Central, Consultorio Norte, etc."
            className="form-field"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Dirección"
            value={sedeFormData.direccion}
            onChange={(e) => setSedeFormData({ ...sedeFormData, direccion: e.target.value })}
            placeholder="Ej: Av. Corrientes 1234, CABA"
            className="form-field"
            disabled={loading}
          />

          <div className="modal-actions">
            <Button onClick={() => setIsSedeModalOpen(false)} className="cancel-button" disabled={loading}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleAddSede} className="custom-button" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Agregar Sede"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={deleteSedeConfirmOpen} onClose={() => !loading && setDeleteSedeConfirmOpen(false)}>
        <div className="delete-modal-container">
          <Typography variant="h6" className="modal-title">
            Confirmar eliminación
          </Typography>
          <Typography className="delete-message">
            ¿Deseas quitar la sede "{sedeToDelete?.nombre}" de tu perfil?
          </Typography>
          <div className="modal-actions">
            <Button onClick={() => setDeleteSedeConfirmOpen(false)} className="cancel-button" disabled={loading}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleDeleteSede} className="custom-button" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Eliminar"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={deleteConfirmOpen} onClose={() => !loading && setDeleteConfirmOpen(false)}>
        <div className="delete-modal-container">
          <Typography variant="h6" className="modal-title">
            Confirmar eliminación
          </Typography>
          <Typography className="delete-message">
            ¿Deseas quitar "{itemToDelete?.nombre}" de tu perfil?
          </Typography>
          <div className="modal-actions">
            <Button onClick={() => setDeleteConfirmOpen(false)} className="cancel-button" disabled={loading}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleDelete} className="custom-button" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Eliminar"}
            </Button>
          </div>
        </div>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} className="snackbar-alert">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ListaServicios;